// Navigation seam.
//
// The MDI tab system was removed; this hook is now a thin wrapper around
// react-router's `useNavigate`. The legacy `{ newTab: true }` option is
// honoured by opening a real browser tab via `window.open(url, "_blank")`,
// preserving the "open in new window" UX without the per-tab MemoryRouter.

import { useCallback } from "react";
import { useNavigate as useRRNavigate } from "react-router";

export interface NavigateOptions {
  /** Open in a real browser tab instead of navigating in place. */
  newTab?: boolean;
}

export type NavigateFn = (url: string, opts?: NavigateOptions) => void;

export function useNavigate(): NavigateFn {
  const rrNavigate = useRRNavigate();

  return useCallback(
    (url: string, opts?: NavigateOptions) => {
      if (opts?.newTab && typeof window !== "undefined") {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      rrNavigate(url);
    },
    [rrNavigate],
  );
}

// ── Entity convenience ─────────────────────────────────────────────────

export type EntityType =
  | "company"
  | "product"
  | "transaction"
  | "transaction_v2"
  | "price_list"
  | "invoice"
  | "invoice_v2"
  | "invoice_reconcile"
  | "employee"
  | "edpr_document"
  | "edpr_request"
  | "bank_batch"
  | "bank_statement_line"
  | "commission_payee"
  | "commission_rule";

/** Pass `"new"` as the id to open the entity's view in create mode. */
export type EntityId = number | "new";

export function getEntityRoute(type: EntityType, id: EntityId): string {
  switch (type) {
    case "company":
      return id === "new" ? `/companies?id=new` : `/companies/${id}`;
    case "product":
      return id === "new" ? `/products?id=new` : `/products/${id}`;
    case "transaction":
    case "transaction_v2":
      return id === "new" ? `/transactions?id=new` : `/transactions/${id}`;
    case "price_list":
      return id === "new" ? `/price-lists?id=new` : `/price-lists/${id}`;
    case "invoice":
    case "invoice_v2":
    case "invoice_reconcile":
      return id === "new" ? `/invoices/received?id=new` : `/invoices/${id}`;
    case "employee":
      return id === "new"
        ? `/settings/employees?id=new`
        : `/settings/employees/${id}`;
    case "edpr_document":
      return id === "new" ? `/edpr?id=new` : `/edpr/${id}`;
    case "edpr_request":
      return id === "new" ? `/edpr-requests?id=new` : `/edpr-requests/${id}`;
    case "bank_batch":
      return `/bank/inbox?batch=${id}`;
    case "bank_statement_line":
      return `/bank/operations/${id}`;
    case "commission_payee":
      return id === "new"
        ? `/commission/payees?id=new`
        : `/commission/payees?id=${id}`;
    case "commission_rule":
      return id === "new"
        ? `/commission/rules?id=new`
        : `/commission/rules?id=${id}`;
  }
}

/**
 * Absolutise an IN-APP route against the origin the caller is running on, or
 * return `undefined` when the target isn't one.
 *
 * Needed wherever a route has to leave the app and still work — today that is
 * the Excel exporters, whose hyperlink cells Excel will only follow if they
 * carry a scheme and host. An export made against prod links to prod, one made
 * on dev links to dev; that matches where the operator actually is, and keeps
 * a dev export from pointing at prod rows that may not exist.
 *
 * Why the guard: this turns a route string into a LIVE LINK inside a file that
 * leaves the building. Not every caller supplies a trusted route — a report's
 * `link_to` is free text authored in the reports admin UI, stored in the DB and
 * interpolated with row data, so a template of `{{some_column}}` hands the
 * report's own SQL output full control of the result. And `new URL(t, base)`
 * passes any absolute-scheme string through untouched: `javascript:alert(1)`
 * stays itself, `//host/x` becomes `http://host/x`. Anything that isn't
 * same-origin http(s) is therefore refused, and callers degrade the cell to
 * plain text rather than emitting a link somewhere we didn't intend.
 */
export function absoluteAppUrl(route: string): string | undefined {
  if (typeof route !== "string") return undefined;
  const target = route.trim();
  if (!target) return undefined;

  const origin = typeof window === "undefined" ? undefined : window.location?.origin;
  if (!origin) return undefined;

  let url: URL;
  try {
    url = new URL(target, origin);
  } catch {
    return undefined;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
  if (url.origin !== origin) return undefined;
  return url.toString();
}

export function useEntityNavigate() {
  const navigate = useNavigate();
  return useCallback(
    (type: EntityType, id: EntityId, opts?: NavigateOptions) => {
      navigate(getEntityRoute(type, id), opts);
    },
    [navigate],
  );
}
